/**
 * HeatmapControl
 *
 * Heatmap for showing data density/magnitude
 * 2D grid with color intensity
 */

import React, { useMemo, memo } from 'react';
import type { ControlRendererProps } from '../ControlRenderer';
import { BaseChartComponent, type ChartDataPoint, extractChartData } from './BaseChartComponent';

const HeatmapControl: React.FC<ControlRendererProps> = memo(({ control, value }) => {
  // CRITICAL: ONLY use control.config.staticData
  const staticData = (control.config?.staticData as ChartDataPoint[] | undefined) || [];
  const chartData = useMemo(() => {
    return staticData; // ONLY use configured data
  }, [staticData]);

  const heatmapConfig = useMemo(() => {
    const config = control.config as Record<string, unknown> | undefined;
    return {
      xKey: (config?.xKey as string) || 'x',
      yKey: (config?.yKey as string) || 'y',
      valueKey: (config?.valueKey as string) || 'value',
      colorScale: (config?.colorScale as string) || 'viridis',
    };
  }, [control.config]);

  const renderHeatmap = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    // Extract unique x and y values
    const xValues = new Set<string>();
    const yValues = new Set<string>();
    const valueMap = new Map<string, number>();

    chartData.forEach((d) => {
      const x = String((d as Record<string, unknown>)[heatmapConfig.xKey] || '');
      const y = String((d as Record<string, unknown>)[heatmapConfig.yKey] || '');
      const val = (d as Record<string, unknown>)[heatmapConfig.valueKey] as number | undefined;

      xValues.add(x);
      yValues.add(y);
      if (typeof val === 'number') {
        valueMap.set(`${x}:${y}`, val);
      }
    });

    const xArray = Array.from(xValues);
    const yArray = Array.from(yValues);

    // Find min/max for color scaling
    const values = Array.from(valueMap.values());
    const minVal = Math.min(...values, 0);
    const maxVal = Math.max(...values, 1);
    const range = maxVal - minVal || 1;

    // Color scale function (viridis-like)
    const getColor = (value: number): string => {
      const normalized = (value - minVal) / range;
      const hue = (1 - normalized) * 240; // 240 to 0 degrees (blue to red)
      return `hsl(${hue}, 80%, 50%)`;
    };

    const cellSize = 40;
    const padding = 60;
    const width = padding + xArray.length * cellSize;
    const height = padding + yArray.length * cellSize;

    return (
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%' }}>
        {/* Cells */}
        {yArray.map((y, yIdx) => {
          return xArray.map((x, xIdx) => {
            const value = valueMap.get(`${x}:${y}`) || 0;
            const cellX = padding + xIdx * cellSize;
            const cellY = padding + yIdx * cellSize;
            const color = getColor(value);

            return (
              <g key={`cell-${xIdx}-${yIdx}`}>
                <rect
                  x={cellX}
                  y={cellY}
                  width={cellSize - 2}
                  height={cellSize - 2}
                  fill={color}
                  opacity="0.8"
                  stroke="#e5e7eb"
                  strokeWidth="1"
                />
                {cellSize > 40 && (
                  <text
                    x={cellX + cellSize / 2}
                    y={cellY + cellSize / 2 + 5}
                    textAnchor="middle"
                    fontSize="11"
                    fill="white"
                    fontWeight="bold"
                  >
                    {value.toFixed(0)}
                  </text>
                )}
              </g>
            );
          });
        })}

        {/* X axis labels */}
        {xArray.map((x, idx) => (
          <text
            key={`x-label-${idx}`}
            x={padding + idx * cellSize + cellSize / 2}
            y={padding - 10}
            textAnchor="middle"
            fontSize="12"
            fill="#6b7280"
          >
            {x.substring(0, 8)}
          </text>
        ))}

        {/* Y axis labels */}
        {yArray.map((y, idx) => (
          <text
            key={`y-label-${idx}`}
            x={padding - 10}
            y={padding + idx * cellSize + cellSize / 2 + 5}
            textAnchor="end"
            fontSize="12"
            fill="#6b7280"
          >
            {y.substring(0, 8)}
          </text>
        ))}

        {/* Legend */}
        <g>
          <text x={padding} y={padding + yArray.length * cellSize + 40} fontSize="11" fill="#6b7280">
            Min: {minVal.toFixed(1)}
          </text>
          <text
            x={padding + xArray.length * cellSize - 50}
            y={padding + yArray.length * cellSize + 40}
            fontSize="11"
            fill="#6b7280"
          >
            Max: {maxVal.toFixed(1)}
          </text>
        </g>
      </svg>
    );
  }, [chartData, heatmapConfig, control.config]);

  return (
    <BaseChartComponent control={control} value={value} chartType="heatmap">
      <div style={{ width: '100%', height: '100%' }}>{renderHeatmap}</div>
    </BaseChartComponent>
  );
});

HeatmapControl.displayName = 'HeatmapControl';

export default HeatmapControl;
